import { useState } from 'react';
import { Table, Tag, Button, App, theme, Input, Select, Space, type TableColumnsType } from 'antd';
import { PlusOutlined, DeleteOutlined, TeamOutlined, CrownOutlined, UserOutlined, StarOutlined } from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useAdminUsers, useRemoveAdminUser } from '@/hooks/useAdminUsers';
import { getUsersStats } from '@/api/usersStats';
import { listAppUsers, ROLE_NAME, type AppUser } from '@/api/appUsers';
import { useAuth } from '@/lib/auth';
import { RequireSuperAdmin } from '@/components/RequireSuperAdmin';
import { AddAdminModal } from './AddAdminModal';
import { UserDetailDrawer } from './UserDetailDrawer';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import type { AdminUser } from '@/api/adminUsers';

function Inner() {
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();
  const q = useAdminUsers();
  const del = useRemoveAdminUser();
  const statsQ = useQuery({ queryKey: ['users-stats'], queryFn: getUsersStats });
  const stats = statsQ.data;
  const { email: myEmail } = useAuth();
  const [addOpen, setAddOpen] = useState(false);

  // Registered (app) users
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [uq, setUq] = useState('');
  const [roleFilter, setRoleFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [selected, setSelected] = useState<AppUser | null>(null);
  const appUsersQ = useQuery({
    queryKey: ['app-users', page, pageSize, uq, roleFilter, statusFilter],
    queryFn: () => listAppUsers({ page, pageSize, q: uq || undefined, role_id: roleFilter, status: statusFilter }),
  });

  function confirmRemove(u: AdminUser) {
    if (u.email === myEmail) {
      message.error("You can't remove yourself");
      return;
    }
    modal.confirm({
      title: `Remove admin ${u.email}?`,
      content: 'They will lose admin access on next login.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await del.mutateAsync(u.email);
          message.success('Removed');
        } catch (e) {
          message.error((e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<AdminUser> = [
    { title: 'Email', dataIndex: 'email', render: (v) => <code>{v}</code> },
    { title: 'Display name', dataIndex: 'display_name', render: (v) => v ?? '—' },
    { title: 'Role', dataIndex: 'role', width: 130, render: (v: string) => (v === 'super_admin' ? <Tag color="gold">super_admin</Tag> : <Tag>admin</Tag>) },
    { title: 'Created', dataIndex: 'created_at', width: 160, render: (v: string) => formatDistanceToNow(new Date(v), { addSuffix: true }) },
    {
      title: 'Actions', width: 100,
      render: (_, u) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmRemove(u)} disabled={u.email === myEmail}>Remove</Button>
      ),
    },
  ];

  const maxRole = Math.max(1, stats?.super_admins ?? 0, stats?.admins ?? 0);
  const Bar = ({ label, count, color }: { label: string; count: number; color: string }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: token.colorTextSecondary }}>{count}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
        <div style={{ height: 8, borderRadius: 4, width: `${Math.round((count / maxRole) * 100)}%`, background: color }} />
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <PageHero
        icon={<TeamOutlined />}
        title="Users"
        subtitle="Admin grants and registered users"
        from="#475569" to="#64748b"
        chips={[
          <HeroChip key="admins">Admins {stats?.total_admins ?? 0}</HeroChip>,
          <HeroChip key="registered">Registered {stats?.registered_users ?? 0}</HeroChip>,
        ]}
        actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add admin</Button>}
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<TeamOutlined />} label="Total admins" value={stats?.total_admins ?? 0} />
        <KpiTile tone="indigo" icon={<CrownOutlined />} label="Super admins" value={stats?.super_admins ?? 0} />
        <KpiTile tone="green" icon={<UserOutlined />} label="Registered" value={stats?.registered_users ?? 0} />
        <KpiTile tone="amber" icon={<StarOutlined />} label="Subscribed" value={stats?.subscribed_users ?? 0} />
      </div>

      <SectionCard tone="slate" icon={<CrownOutlined />} title="Admins by role" desc="Super-admins vs admins">
        <Bar label="super_admin" count={stats?.super_admins ?? 0} color="#6366f1" />
        <Bar label="admin" count={stats?.admins ?? 0} color="#64748b" />
      </SectionCard>

      <SectionCard tone="slate" icon={<TeamOutlined />} title="Admin users" desc="Grant or revoke admin access">
        <Table
          rowKey="email"
          loading={q.isLoading}
          columns={columns}
          dataSource={q.data ?? []}
          pagination={false}
        />
      </SectionCard>

      <SectionCard tone="slate" icon={<UserOutlined />} title="Registered users" desc="App end users — view detail, change role, subscription, activate/deactivate, delete">
        <Space style={{ marginBottom: 12 }} wrap>
          <Input.Search allowClear placeholder="email or username" style={{ width: 240 }} onSearch={(v) => { setUq(v); setPage(1); }} />
          <Select
            allowClear placeholder="Role" style={{ width: 150 }} value={roleFilter}
            options={Object.entries(ROLE_NAME).map(([v, l]) => ({ value: Number(v), label: l }))}
            onChange={(v) => { setRoleFilter(v); setPage(1); }}
          />
          <Select
            allowClear placeholder="Status" style={{ width: 130 }} value={statusFilter}
            options={[{ value: 'active', label: 'active' }, { value: 'inactive', label: 'inactive' }]}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
        </Space>
        <Table<AppUser>
          rowKey="id"
          size="small"
          loading={appUsersQ.isLoading}
          dataSource={appUsersQ.data?.data ?? []}
          onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: 'pointer', opacity: r.is_active ? 1 : 0.55 } })}
          pagination={{
            current: page, pageSize, total: appUsersQ.data?.total ?? 0,
            showSizeChanger: true, pageSizeOptions: ['20', '50', '100'], showTotal: (t) => `${t} users`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          columns={[
            { title: 'Email', dataIndex: 'email', ellipsis: true, render: (v) => <code>{v}</code> },
            { title: 'Username', dataIndex: 'username', width: 140, render: (v) => v ?? '—' },
            { title: 'Role', width: 120, render: (_, u) => ROLE_NAME[u.role_id] ?? u.role_id },
            { title: 'Status', width: 90, render: (_, u) => <Tag color={u.is_active ? 'success' : 'default'}>{u.is_active ? 'active' : 'inactive'}</Tag> },
            { title: 'Subscribed', width: 100, render: (_, u) => (u.is_subscribed ? <Tag color="blue">yes</Tag> : '—') },
            { title: 'Created', width: 150, render: (_, u) => formatDistanceToNow(new Date(u.created_at), { addSuffix: true }) },
          ]}
        />
      </SectionCard>

      <AddAdminModal open={addOpen} onClose={() => setAddOpen(false)} />
      <UserDetailDrawer user={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export function UsersListPage() {
  return (
    <RequireSuperAdmin>
      <Inner />
    </RequireSuperAdmin>
  );
}
